import React, {useEffect, useRef, useState} from "react";
import {useAuth} from "../auth";
import {ApiError} from "../api/client";
import {formatMerchantApiError} from "../api/merchant";
import {ReceiptTarget, uploadPortalReceipt} from "../api/merchantPortalPayin";
import {rejectFile} from "./payment-details/logic";

type Draft = {id: number; file: File; uncertain: boolean};

/** Files stay in this browser until the merchant explicitly sends them. */
export function MerchantReceiptDrafts({orderId, target, label = "Прикрепить чек", disabled = false, onChanged, onBusyChange}: {
    orderId: string; target: ReceiptTarget; label?: string; disabled?: boolean;
    onChanged?: () => Promise<unknown> | void; onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
    const {token} = useAuth();
    const scope = `${orderId}:${target.paymentId}:${target.attemptId}:${target.requisitesVersion}:${target.partNumber ?? 0}:${target.partRevision ?? 1}`;
    const currentScope = useRef(scope); currentScope.current = scope;
    const previousScope = useRef(scope);
    const sequence = useRef(0);
    const sending = useRef(false);
    const [drafts, setDrafts] = useState<Draft[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    useEffect(() => {
        if (previousScope.current !== scope) {
            setDrafts([]); setError("");
            setNotice("Реквизиты изменились. Проверьте получателя и выберите чеки заново.");
            previousScope.current = scope;
        }
    }, [scope]);

    const send = async (): Promise<void> => {
        if (sending.current || disabled || !drafts.length || previousScope.current !== scope) return;
        sending.current = true; setBusy(true); onBusyChange?.(true); setError(""); setNotice("");
        const selected = [...drafts];
        let delivered = false;
        try {
            for (const draft of selected) {
                if (currentScope.current !== scope) break;
                try {
                    await uploadPortalReceipt(token, orderId, target, draft.file);
                    delivered = true;
                    if (currentScope.current === scope) setDrafts((items) => items.filter((item) => item.id !== draft.id));
                } catch (err) {
                    if (currentScope.current !== scope) break;
                    const uncertain = !(err instanceof ApiError) || err.status >= 500;
                    setDrafts((items) => items.map((item) => item.id === draft.id ? {...item, uncertain} : item));
                    setError(`${draft.file.name}: ${formatMerchantApiError(err)}${uncertain ? " Ответ не получен. Повторите отправку этого файла для проверки результата." : ""}`);
                    break;
                }
            }
            if (delivered) {
                if (currentScope.current === scope) setNotice("Принятые чеки отправлены трейдеру.");
                try { await onChanged?.(); } catch { if (currentScope.current === scope) setError("Чеки отправлены. Не удалось обновить состояние заявки — обновите страницу."); }
            }
        } finally {
            sending.current = false; setBusy(false); onBusyChange?.(false);
        }
    };

    return <div className="merchant-receipt-drafts" aria-busy={busy}>
        <label className="receipt-dropzone merchant-receipt-picker" data-uploading={busy}><strong>{label}</strong><input className="merchant-receipt-picker-input" type="file" multiple aria-label={label} accept=".pdf,.jpg,.jpeg,.png,.webp" disabled={disabled || busy} onChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []); event.currentTarget.value = "";
            setError(""); setNotice("");
            const invalid = files.map(rejectFile).filter(Boolean);
            if (invalid.length) { setError(invalid.join(" ")); return; }
            setDrafts((items) => [...items, ...files.map((file) => ({id: ++sequence.current, file, uncertain: false}))]);
        }}/></label>
        <small>PDF, JPG, PNG или WebP до 15 MiB. До отправки файл можно удалить и выбрать другой.</small>
        {drafts.length > 0 && <ul>{drafts.map((draft) => <li key={draft.id}><span>{draft.file.name}</span>{" "}<button type="button" className="btn btn-ghost btn-sm" disabled={disabled || busy || draft.uncertain} aria-label={`Удалить ${draft.file.name}`} onClick={() => setDrafts((items) => items.filter((item) => item.id !== draft.id))}>Удалить</button></li>)}</ul>}
        <div className="requisites-actions"><button type="button" className="btn btn-primary" disabled={disabled || busy || !drafts.length} onClick={() => void send()}>{busy ? "Отправляем…" : "Отправить чеки"}</button></div>
        {notice && <p role="status">{notice}</p>}
        {error && <div role="alert" className="alert alert-warning">{error}</div>}
    </div>;
}
